import { type AudioFrame } from './audio-frame.js';
import { expSmooth } from './math.js';

/**
 * An envelope that jumps to the beat intensity on every detected beat
 * and decays exponentially back to zero. The standard building block
 * for "flash on the kick, then fade" behaviour.
 */
export class BeatPulse {
  private readonly halfLifeSeconds: number;
  private value = 0;

  constructor(halfLifeSeconds: number) {
    this.halfLifeSeconds = halfLifeSeconds;
  }

  public update(frame: AudioFrame, deltaSeconds: number): number {
    this.value = expSmooth(this.value, 0, deltaSeconds, this.halfLifeSeconds);
    if (frame.beat) {
      this.value = Math.max(this.value, frame.beatIntensity);
    }
    return this.value;
  }

  public get current(): number {
    return this.value;
  }
}

/**
 * An organic breathing envelope with dual-phase smoothing: a gentle ease-in
 * attack curve on beats and a buoyant, gradual relaxation decay.
 * This converts sharp impulses into natural hydrostatic respiration waves.
 */
export class BreathingPulse {
  private readonly attackHalfLife: number;
  private readonly decayHalfLife: number;
  private value = 0;
  private target = 0;

  constructor(attackHalfLife = 0.08, decayHalfLife = 0.52) {
    this.attackHalfLife = attackHalfLife;
    this.decayHalfLife = decayHalfLife;
  }

  public update(frame: AudioFrame, deltaSeconds: number): number {
    this.target = expSmooth(this.target, 0, deltaSeconds, this.decayHalfLife);
    if (frame.beat) {
      this.target = Math.min(1.2, Math.max(this.target, frame.beatIntensity));
    }
    const halfLife = this.value < this.target ? this.attackHalfLife : this.decayHalfLife;
    this.value = expSmooth(this.value, this.target, deltaSeconds, halfLife);
    return this.value;
  }

  public get current(): number {
    return this.value;
  }
}
